import { ReactNode } from 'react'
import { QueryClient, QueryClientProvider, QueryCache, isServer } from '@tanstack/react-query'
import { TamaguiProvider } from '@tamagui/core'
import { ThemeProvider } from '@fe-toolkit/web-components/ssr'
import { tamaguiConfig } from '@fe-toolkit/ui'
import { createLogger } from '@fe-toolkit/observability/client'
import { ClientTrackingContextProvider } from '@fe-toolkit/utility/client/tracking-context'

import { RuntimeConfig, basePath } from '@/config'

const logger = createLogger({ name: 'QueryClient' })

function makeQueryClient() {
  return new QueryClient({
    queryCache: new QueryCache({
      onError: (error, query) => {
        logger.error('Query failed', {
          error,
          queryKey: query.queryKey,
          queryHash: query.queryHash,
        })
      },
    }),
    defaultOptions: {
      queries: {
        staleTime: 60 * 1000,
      },
    },
  })
}

let browserQueryClient: QueryClient | undefined = undefined

function getQueryClient() {
  if (isServer) {
    // Server: always make a new query client
    return makeQueryClient()
  } else {
    // Browser: make a new query client if we don't already have one
    if (!browserQueryClient) browserQueryClient = makeQueryClient()

    return browserQueryClient
  }
}

type ProvidersProps = {
  children: ReactNode
  runtimeConfig: RuntimeConfig
  memberId?: number
}

export default function Providers({ children, runtimeConfig, memberId }: ProvidersProps) {
  const queryClient = getQueryClient()

  return (
    <QueryClientProvider client={queryClient}>
      <ClientTrackingContextProvider
        config={{
          baseUrl: runtimeConfig.API_GATEWAY_URL,
          memberId,
          mfeBasePath: basePath,
          withDebug: process.env.NODE_ENV === 'development',
        }}
      >
        <TamaguiProvider config={tamaguiConfig} defaultTheme="light">
          <ThemeProvider>{children}</ThemeProvider>
        </TamaguiProvider>
      </ClientTrackingContextProvider>
    </QueryClientProvider>
  )
}
